import { Box, Button, Tooltip, Typography, styled } from '@mui/material'
import debounce from 'lodash-es/debounce'
import { ApplicationTypes, type FetchPriceParams, KickPlate } from 'metalworks-domain'
import { useObservableEagerState } from 'observable-hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'
import { useMetalworksApi } from '../systems/metalworks-api'
import { ExportButton } from './finalization/ExportButton'

export const SummaryDetailRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
}))

const FooterCurrencySmall = styled(Typography)(() => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
  letterSpacing: -0.3,
}))

const Aud = styled('span')(({ theme }) => ({
  marginInlineEnd: theme.spacing(0.25),
}))

const priceFormatter = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const PriceInfo = () => {
  const { rootEntityId, navigationSystem, storeSystem } = configuratorInterface.useDomain()
  const logger = useLogger()

  const [price, setPrice] = useState<number | null>(null)
  const [error, setError] = useState<unknown | null>(null)

  const plate = configuratorInterface.useEntityState(rootEntityId)
  const navigationState = useObservableEagerState(navigationSystem.activeNavigationState$)

  const metalworksApi = useMetalworksApi()

  const handleBulkUploadClick = useCallback(() => {
    storeSystem.stageAndCommit([
      navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.BulkFinalization),
    ])
  }, [navigationSystem, storeSystem])

  // Prepare debounced version of the "fetch price" function.
  const requestPrice = useMemo(
    () =>
      debounce(
        (params: FetchPriceParams) => {
          metalworksApi
            .fetchPrice(params)
            .then(price => {
              setPrice(price)
              setError(null)
            })
            .catch(err => {
              setError(err)

              if (err instanceof Error) {
                logger.error('Fetch price error', {}, err)
              } else {
                logger.error('Fetch price error', { err })
              }
            })
        },
        1000,
        { leading: true, trailing: true }
      ),
    [metalworksApi, logger]
  )

  const numHoles = useMemo(
    () => {
      return plate.$numHoles
    },
    // Memoize `$numHoles` by checking all its keys and values.
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    [Object.keys(plate.$numHoles).join('-'), Object.values(plate.$numHoles).join('-')]
  )

  useEffect(() => {
    const materialCode = plate.$selectedCustomMaterial?.code
    if (!materialCode) {
      throw new Error('Custom material not found. This should happen only in dry-run mode.')
    }

    requestPrice({
      height: plate.$sizeY,
      width: plate.$sizeX,
      quantity: plate.quantity,
      material: materialCode,
      holes: numHoles,
      numBends: plate.$numBends,
      powderCoating: plate.powderCoating,
    })
  }, [
    plate.$sizeX,
    plate.$sizeY,
    plate.quantity,
    plate.$selectedCustomMaterial?.code,
    requestPrice,
    numHoles,
    plate.$numBends,
    plate.powderCoating,
  ])

  const exportButton = phoenix.runtimeConfig.DEBUG && <ExportButton />
  const bulkUploadButton = KickPlate.is(plate) && navigationState === ApplicationTypes.NavigationState.Summary && (
    <Button onClick={handleBulkUploadClick} fullWidth variant="outlined">
      Bulk Order Kick Plates
    </Button>
  )

  if (error) {
    return (
      <>
        <Typography variant="body2" color="secondary">
          Could not calculate price. Please try again later.
        </Typography>
        {bulkUploadButton}
        {exportButton}
      </>
    )
  }

  if (!price) {
    return null
  }

  return (
    <>
      <SummaryDetailRow>
        {/* TODO: Estimated price per plate only outside of summary? */}
        <Typography variant="body2" color="secondary">
          Estimated price per plate (ex GST)
        </Typography>
        <FooterCurrencySmall>
          <Aud title="Australian Dollar sign">$</Aud>
          {priceFormatter.format(price)}
        </FooterCurrencySmall>
      </SummaryDetailRow>
      <SummaryDetailRow>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="secondary">
            Estimated total price (ex GST)
          </Typography>
          <Tooltip title="Surcharges may apply. Final pricing determined in Daveweb." placement="top" arrow>
            {/* Tooltip can't wrap SVG icon directly. */}
            <div style={{ lineHeight: 0 }}>
              <PhxIcon name="WarningCircleDuotone" />
            </div>
          </Tooltip>
        </Box>
        <FooterCurrencySmall>
          <Aud title="Australian Dollar sign">$</Aud>
          {priceFormatter.format(price * plate.quantity)}
        </FooterCurrencySmall>
      </SummaryDetailRow>
      {bulkUploadButton}
      {exportButton}
    </>
  )
}
